import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaPaperPlane, FaUser, FaHeart, FaTimes, FaMagic, FaEnvelope, FaLock } from 'react-icons/fa';
import HeartLoader from '../components/HeartLoader';
import { isBengaliText } from '../utils/textUtils';

const getRandomTagline = () => {
  const taglines = [
    "✨ Want your own magical message box?",
    "💌 Create your secret letter collection!",
    "🌟 Ready to receive your own mystery messages?",
    "💭 Curious what others would tell you?",
    "✉️ Start your anonymous adventure today!"
  ];
  return taglines[Math.floor(Math.random() * taglines.length)];
};

const SendMessage = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [tagline] = useState(getRandomTagline());
  const { username } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecipient = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${username}`);
        if (!response.ok) {
          throw new Error('User not found');
        }
        const data = await response.json();
        setRecipient(data);
      } catch (err) {
        setError('Invalid username');
      } finally {
        setLoadingUser(false);
      }
    };

    fetchRecipient();
  }, [username]);

  useEffect(() => {
    if (recipient) {
      document.title = `Send message to ${recipient.username} | Chithi.Pii`;
    } else {
      document.title = 'Send Message | Chithi.Pii';
    }
  }, [recipient]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      return setError('Message cannot be empty');
    }

    try {
      setError('');
      setLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/messages/${username}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: message }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSuccess(true);
      setMessage('');
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-indigo-100 flex items-center justify-center">
        <HeartLoader size="large" />
      </div>
    );
  }

  if (!recipient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-3xl opacity-75 group-hover:opacity-100 transition duration-300 blur"></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-white/20">
              <div className="text-center">
                <div className="mb-6 relative">
                  <div className="w-16 h-16 mx-auto bg-red-500/10 rounded-full flex items-center justify-center">
                    <FaTimes className="w-8 h-8 text-red-500 animate-pulse" />
                  </div>
                  <div className="absolute top-0 -left-4 w-4 h-4 bg-pink-500/20 rounded-full animate-float-slow"></div>
                  <div className="absolute bottom-0 -right-4 w-6 h-6 bg-purple-500/20 rounded-full animate-float-delayed"></div>
                </div>

                <h2 className="text-3xl font-bold text-white mb-4">Invalid Link</h2>
                <p className="text-white/80 mb-8">
                  This message link is invalid or has expired.
                </p>

                <div className="space-y-6">
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Create Your Own Link!
                    </h3>
                    <p className="text-white/70 mb-4">
                      Get your personal anonymous message box and start receiving heartfelt messages from friends.
                    </p>
                    <Link
                      to="/register"
                      className="inline-flex items-center px-6 py-3 bg-white text-purple-600 rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                      <FaMagic className="w-5 h-5 mr-2" />
                      Get Started Free
                    </Link>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div className="flex items-start space-x-2">
                      <div className="bg-white/10 rounded-lg p-2">
                        <FaHeart className="w-4 h-4 text-pink-300" />
                      </div>
                      <span className="text-sm text-white/80">Anonymous Messages</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="bg-white/10 rounded-lg p-2">
                        <FaUser className="w-4 h-4 text-purple-300" />
                      </div>
                      <span className="text-sm text-white/80">Custom Profile</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="bg-white/10 rounded-lg p-2">
                        <FaEnvelope className="w-4 h-4 text-indigo-300" />
                      </div>
                      <span className="text-sm text-white/80">Instant Delivery</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="bg-white/10 rounded-lg p-2">
                        <FaLock className="w-4 h-4 text-blue-300" />
                      </div>
                      <span className="text-sm text-white/80">Secure & Private</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${Math.random() * 3 + 2}s`
                }}
              >
                <FaHeart
                  className="text-white/10"
                  style={{
                    fontSize: `${Math.random() * 20 + 10}px`,
                    transform: `rotate(${Math.random() * 360}deg)`
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-indigo-100 flex flex-col">
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl opacity-75 group-hover:opacity-100 transition duration-300 blur"></div>
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-600 opacity-90"></div>
                <div className="absolute top-4 left-4 animate-float-slow">
                  <FaHeart className="w-6 h-6 text-pink-200 opacity-50" />
                </div>
                <div className="absolute bottom-4 right-4 animate-float-delayed">
                  <FaHeart className="w-8 h-8 text-purple-200 opacity-50" />
                </div>
                <div className="relative px-6 py-10 text-center">
                  <div className="inline-block p-2 rounded-full bg-white/10 backdrop-blur-sm mb-4 transform group-hover:scale-105 transition-transform duration-300">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-r from-pink-200 to-purple-200 flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                      <FaUser className="w-12 h-12 text-gray-600" />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2 transform group-hover:scale-105 transition-transform duration-300">
                    {recipient.username}
                  </h2>
                  <p className="text-white/80 text-sm">
                    Send me anonymous message
                  </p>
                </div>
              </div>

              <div className="p-6">
                {error && (
                  <div className="mb-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="mb-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded animate-pulse">
                    Message sent successfully!
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="relative">
                    <textarea
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Write your message..."
                      className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none h-32 ${
                        isBengaliText(message) 
                          ? 'font-bengaliHandwriting text-xl leading-relaxed'
                          : 'font-vicky text-2xl leading-relaxed'
                      }`}
                      rows={6}
                      maxLength={500}
                    />
                    <div className="absolute bottom-3 right-3 text-sm text-purple-400 font-medium bg-white/80 px-2 py-1 rounded-full">
                      {message.length}/500
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || success}
                    className={`w-full flex items-center justify-center px-6 py-4 border border-transparent text-lg font-medium rounded-xl text-white transform hover:scale-105 transition-all duration-200 ${
                      loading || success
                        ? 'bg-gray-400'
                        : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500`}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <FaPaperPlane className="w-5 h-5 mr-2" />
                        {success ? 'Sent!' : 'Send Message'}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="relative mt-12 text-center">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-400 blur-lg opacity-20"></div>
              </div>
            </div>

            <br />

            <div className="mt-8">
              <p className="font-vicky text-2xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                {tagline}
              </p>
              <Link 
                to="/register" 
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105 shadow-lg"
              >
                <FaHeart className="w-5 h-5 mr-2" />
                Create Your Own Link
              </Link>
            </div>

            <div className="absolute -bottom-4 left-1/4 transform -translate-x-1/2">
              <FaHeart className="w-8 h-8 text-pink-400 opacity-50" />
            </div>
            <div className="absolute -top-2 right-1/4 transform translate-x-1/2">
              <FaHeart className="w-6 h-6 text-purple-400 opacity-50" />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SendMessage;